import React, { useState, useEffect } from 'react';
import { Mail, Share2, MessageSquare, Image, Plus, Trash2, ExternalLink, FileText, Eye, FileDown } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import html2canvas from 'html2canvas';

const MarketingTab = ({ BACKEND_URL }) => {
  const [savedAds, setSavedAds] = useState([]);
  const [posBrochures, setPosBrochures] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showAddAdModal, setShowAddAdModal] = useState(false);
  const [showEditBrochureModal, setShowEditBrochureModal] = useState(false);
  const [showCreateBrochureModal, setShowCreateBrochureModal] = useState(false);
  const [showViewBrochureModal, setShowViewBrochureModal] = useState(false);
  const [viewingBrochure, setViewingBrochure] = useState(null);
  const [editingBrochure, setEditingBrochure] = useState(null);
  const [brochureForm, setBrochureForm] = useState({ title: '', description: '', content: '', category: 'POS Materials' });
  const [newAd, setNewAd] = useState({ name: '', platform: '', imageUrl: '', notes: '', htmlContent: '' });
  const [showEditAdModal, setShowEditAdModal] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [editAdForm, setEditAdForm] = useState({ name: '', platform: '', imageUrl: '', notes: '', htmlContent: '' });
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const fetchSavedAds = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/ads`);
      if (!response.ok) throw new Error('Failed to fetch ads');
      const data = await response.json();
      setSavedAds(data);
    } catch (err) {
      console.error('Error fetching ads:', err.message);
      setSavedAds([]);
    }
  };

  const fetchPosBrochures = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/brochures`);
      if (!response.ok) throw new Error('Failed to fetch brochures');
      const data = await response.json();
      setPosBrochures(data);
    } catch (err) {
      console.error('Error fetching brochures:', err.message);
      setPosBrochures([]);
    }
  };

  useEffect(() => {
    fetchSavedAds();
    fetchPosBrochures();
  }, []);

  const handleViewBrochure = (brochure) => {
    setViewingBrochure(brochure);
    setShowViewBrochureModal(true);
  };

  const handleBrochureToPDF = async (brochure) => {
    const element = document.getElementById('pdf-content');
    if (!element) return;
    setIsGeneratingPdf(true);
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `${brochure.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    try {
      await html2pdf().set(opt).from(element).save();
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    }
    setIsGeneratingPdf(false);
  };

  const handleBrochureToImage = async (brochure, format = 'png', target = 'portrait') => {
    // Prefer the actual ad artwork element; fall back to the whole preview
    const root = document.getElementById('pdf-content');
    if (!root) return;
    const selector = target === 'landscape' ? '.ml-ad-landscape' : '.ml-ad-portrait';
    const source = root.querySelector(selector) || root.querySelector('.ml-ad-artwork') || root;
    setIsGeneratingPdf(true);
    const safeName = (brochure.title || 'ad').replace(/[^a-zA-Z0-9]/g, '_');

    // Build a detached mirror of the ad outside the modal (avoids portal / overlay
    // interference that makes html2canvas throw 'Unable to find element in cloned iframe').
    const stage = document.createElement('div');
    stage.style.cssText = 'position:fixed;left:-99999px;top:0;pointer-events:none;background:#0f2818;';
    stage.innerHTML = source.outerHTML;
    document.body.appendChild(stage);
    const clone = stage.firstElementChild;

    try {
      // Wait for any <img> tags in the clone to load
      const imgs = Array.from(clone.querySelectorAll('img'));
      await Promise.all(imgs.map(img =>
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise(res => { img.onload = img.onerror = () => res(); })
      ));
      await new Promise(r => setTimeout(r, 250));

      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        width: clone.offsetWidth,
        height: clone.offsetHeight,
      });
      const mime = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const dataUrl = canvas.toDataURL(mime, 0.95);
      const link = document.createElement('a');
      link.download = `${safeName}_${target}.${format === 'jpeg' ? 'jpg' : 'png'}`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Image generation failed:', error);
      alert(`Failed to generate ${format.toUpperCase()}. Try a different browser if it persists.`);
    } finally {
      document.body.removeChild(stage);
      setIsGeneratingPdf(false);
    }
  };

  const handleEditBrochure = (brochure) => {
    setEditingBrochure(brochure);
    setBrochureForm({ title: brochure.title || '', description: brochure.description || '', content: brochure.content || '', category: brochure.category || 'POS Materials' });
    setShowEditBrochureModal(true);
  };

  const handleSaveBrochure = async () => {
    if (!editingBrochure) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/brochures/${editingBrochure.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(brochureForm)
      });
      if (response.ok) {
        alert('Brochure saved successfully!');
        setShowEditBrochureModal(false);
        setEditingBrochure(null);
        fetchPosBrochures();
      } else { alert('Failed to save brochure'); }
    } catch (error) { alert('Error saving brochure'); }
  };

  const handleCreateBrochure = async () => {
    if (!brochureForm.title) { alert('Please enter a title'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/brochures`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(brochureForm)
      });
      if (response.ok) {
        alert('Brochure created successfully!');
        setShowCreateBrochureModal(false);
        setBrochureForm({ title: '', description: '', content: '', category: 'POS Materials' });
        fetchPosBrochures();
      } else { alert('Failed to create brochure'); }
    } catch (error) { alert('Error creating brochure'); }
  };

  const handleDeleteBrochure = async (brochureId) => {
    if (!window.confirm('Delete this brochure?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/brochures/${brochureId}`, { method: 'DELETE' });
      if (response.ok) { alert('Brochure deleted'); fetchPosBrochures(); }
    } catch (error) { console.error('Error deleting brochure:', error); }
  };

  const handleSaveAd = async () => {
    if (!newAd.name) { alert('Please enter an ad name'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/ads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newAd)
      });
      if (!response.ok) throw new Error('Failed to save ad');
      const savedAd = await response.json();
      setSavedAds([...savedAds, savedAd]);
      setNewAd({ name: '', platform: '', imageUrl: '', notes: '' });
      setShowAddAdModal(false);
      alert('Ad saved successfully!');
    } catch (err) { alert('Error saving ad: ' + err.message); }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm('Are you sure you want to delete this ad?')) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/ads/${adId}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete ad');
      setSavedAds(savedAds.filter(ad => ad.id !== adId));
      alert('Ad deleted successfully!');
    } catch (err) { alert('Error deleting ad: ' + err.message); }
  };

  const handleEditAd = (ad) => {
    setEditingAd(ad);
    setEditAdForm({
      name: ad.name || '',
      platform: ad.platform || '',
      imageUrl: ad.imageUrl || '',
      notes: ad.notes || '',
      htmlContent: ad.htmlContent || '',
    });
    setShowEditAdModal(true);
  };

  const handleSaveEditedAd = async () => {
    if (!editingAd) return;
    if (!editAdForm.name) { alert('Ad name is required'); return; }
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/marketing/ads/${editingAd.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editAdForm),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save ad');
      }
      const updated = await response.json();
      setSavedAds(savedAds.map(a => a.id === updated.id ? updated : a));
      setShowEditAdModal(false);
      setEditingAd(null);
      alert('Ad updated successfully!');
    } catch (err) {
      alert('Error saving ad: ' + err.message);
    }
  };

  const handleShareBrochure = async (brochure, method = 'native') => {
    const brochureUrl = `${BACKEND_URL}/api/brochure/${brochure.id}`;
    const shareTitle = `${brochure.title} - Measured Livestock`;
    if (method === 'email') {
      const subject = encodeURIComponent(shareTitle);
      const body = encodeURIComponent(`Hi,\n\nI wanted to share this with you:\n\n${brochure.title}\n${brochure.description || ''}\n\nView here: ${brochureUrl}\n\nRegards,\nMeasured Livestock`);
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } else if (method === 'sms') {
      const smsBody = `${brochure.title}\n\nView: ${brochureUrl}`;
      window.location.href = `sms:?body=${encodeURIComponent(smsBody)}`;
    } else if (navigator.share) {
      try { await navigator.share({ title: shareTitle, text: brochure.description || brochure.title, url: brochureUrl }); } catch (err) { if (err.name !== 'AbortError') console.log('Share failed:', err); }
    } else {
      try { await navigator.clipboard.writeText(`${shareTitle}\n${brochureUrl}`); alert('Link copied to clipboard!'); } catch (err) { alert(`Share this link:\n${brochureUrl}`); }
    }
  };

  const categoryOptions = ['POS Materials', 'Logos & Branding', 'Social Media', 'Promotional Materials', 'Icons & Graphics', 'Internal Document', 'Brochure', 'Business Cards'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Farmer Services Brochure — quick-access PDF for staff */}
      <div className="bg-gradient-to-r from-[#1a4d2e] to-[#2d5a3d] rounded-lg shadow-md p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold mb-1 flex items-center gap-2">
              <FileDown className="w-5 h-5 text-[#ff6b00]" />
              Farmer Services Brochure (PDF)
            </h3>
            <p className="text-white/85 text-sm">
              A clean, branded one-pager covering our services, why good Animal IDs matter, and directing
              farmers to the PriceHub. Share with staff or hand out to farmers.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a
              href={`${BACKEND_URL}/api/marketing/farmer-services-pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#ff6b00] text-white px-4 py-2 rounded-lg hover:bg-[#ff6b00]/90 transition-colors font-semibold"
              data-testid="download-farmer-pdf-btn"
            >
              <FileDown className="w-4 h-4" /> Download PDF
            </a>
            <button
              onClick={() => {
                const url = `${window.location.origin}${`${BACKEND_URL}/api/marketing/farmer-services-pdf`.replace(window.location.origin, '')}`;
                const link = `${BACKEND_URL}/api/marketing/farmer-services-pdf`;
                navigator.clipboard.writeText(link);
                alert('PDF link copied — share with staff!');
              }}
              className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white border border-white/30 px-4 py-2 rounded-lg transition-colors"
              data-testid="copy-farmer-pdf-link-btn"
            >
              <Share2 className="w-4 h-4" /> Copy Link
            </button>
          </div>
        </div>
      </div>

      {/* Saved Ads Section */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 data-testid="marketing-title" className="text-2xl font-bold text-[#1a4d2e]">Marketing Materials</h2>
            <p className="text-gray-600">Save and manage your social media ads and marketing materials</p>
          </div>
          <div className="flex gap-3">
            <a href="/social-ads" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#ff6b00] text-white px-4 py-2 rounded-lg hover:bg-[#ff6b00]/90 transition-colors">
              <ExternalLink className="w-4 h-4" /> View Ad Templates
            </a>
            <button onClick={() => setShowAddAdModal(true)} data-testid="save-new-ad-btn" className="flex items-center gap-2 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg hover:bg-[#1a4d2e]/90 transition-colors">
              <Plus className="w-4 h-4" /> Save New Ad
            </button>
          </div>
        </div>
        {savedAds.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">No Saved Ads Yet</h3>
            <p className="text-gray-500 mb-4">Save your marketing materials here for easy access</p>
            <button onClick={() => setShowAddAdModal(true)} className="inline-flex items-center gap-2 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg hover:bg-[#1a4d2e]/90 transition-colors">
              <Plus className="w-4 h-4" /> Add Your First Ad
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedAds.map((ad) => (
              <div key={ad.id} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                {ad.imageUrl ? (
                  <img src={ad.imageUrl} alt={ad.name} className="w-full h-48 object-cover bg-gray-100" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                ) : null}
                <div className={`w-full h-48 bg-gradient-to-br from-[#1a4d2e] to-[#2d5a3d] items-center justify-center text-white ${ad.imageUrl ? 'hidden' : 'flex'}`}>
                  <Image className="w-12 h-12 opacity-50" />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#1a4d2e] mb-1">{ad.name}</h3>
                  {ad.platform && <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded mb-2">{ad.platform}</span>}
                  {ad.notes && <p className="text-sm text-gray-500 line-clamp-2">{ad.notes}</p>}
                  <div className="flex justify-between items-center mt-3 pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleViewBrochure({
                          id: ad.id, title: ad.name, description: ad.platform || 'Saved Ad', category: ad.platform || 'Ads',
                          content: ad.htmlContent
                            ? ad.htmlContent
                            : ad.imageUrl
                              ? `<div style="text-align: center;"><img src="${ad.imageUrl}" alt="${ad.name}" style="max-width: 100%; border-radius: 8px;" />${ad.notes ? `<p style="margin-top: 15px; color: #666;">${ad.notes}</p>` : ''}</div>`
                              : `<div style="padding: 30px; background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%); border-radius: 12px;"><div style="text-align: center; margin-bottom: 20px;"><span style="font-size: 48px;">Document</span></div><h3 style="color: #1a4d2e; font-size: 20px; font-weight: bold; margin-bottom: 15px; text-align: center;">${ad.name}</h3>${ad.platform ? `<p style="text-align: center; margin-bottom: 15px;"><span style="background: #1a4d2e; color: white; padding: 4px 12px; border-radius: 20px; font-size: 12px;">${ad.platform}</span></p>` : ''}${ad.notes ? `<div style="background: white; padding: 20px; border-radius: 8px; margin-top: 15px; border-left: 4px solid #ff6b00;"><p style="color: #333; line-height: 1.6; white-space: pre-wrap;">${ad.notes}</p></div>` : ''}<p style="text-align: center; margin-top: 20px; color: #999; font-size: 13px;">To add viewable content, edit this document and add HTML content or an image URL.</p></div>`
                        })}
                        data-testid={`view-ad-${ad.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-[#1a4d2e] text-white rounded-lg text-sm hover:bg-[#2d5a3d]"
                      >
                        <Eye className="w-4 h-4" /> View
                      </button>
                      <button
                        onClick={() => handleEditAd(ad)}
                        data-testid={`edit-ad-${ad.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                      >
                        Edit
                      </button>
                      <span className="text-xs text-gray-400">{new Date(ad.created_at).toLocaleDateString()}</span>
                    </div>
                    <button onClick={() => handleDeleteAd(ad.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* All Marketing Materials (Brochures) Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
          <h2 className="text-xl font-bold text-[#1a4d2e] flex items-center gap-2">
            <FileText className="w-5 h-5" /> All Marketing Materials
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
              <option value="All">All Categories</option>
              {categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            <button onClick={() => { setBrochureForm({ title: '', description: '', content: '', category: 'POS Materials' }); setShowCreateBrochureModal(true); }} data-testid="create-document-btn" className="flex items-center gap-2 bg-[#1a4d2e] text-white px-4 py-2 rounded-lg hover:bg-[#2d5a3d]">
              <Plus className="w-4 h-4" /> Create Document
            </button>
          </div>
        </div>

        {posBrochures.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No documents available yet.</p>
            <p className="text-sm text-gray-400 mt-2">Click "Create Document" to add your first one</p>
          </div>
        ) : (
          <>
            {/* Category count summary */}
            <div className="flex flex-wrap gap-2 mb-4">
              {(() => {
                const categories = {};
                posBrochures.forEach(b => { const cat = b.category || 'Uncategorized'; categories[cat] = (categories[cat] || 0) + 1; });
                return Object.entries(categories).map(([cat, count]) => (
                  <button key={cat} onClick={() => setSelectedCategory(cat === selectedCategory ? 'All' : cat)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat ? 'bg-[#1a4d2e] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{cat} ({count})</button>
                ));
              })()}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {posBrochures.filter(brochure => selectedCategory === 'All' || brochure.category === selectedCategory).map((brochure) => (
                <div key={brochure.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{brochure.thumbnail || 'Doc'}</div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-gray-800">{brochure.title}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                          brochure.category === 'Logos & Branding' ? 'bg-purple-100 text-purple-700' :
                          brochure.category === 'Social Media' ? 'bg-blue-100 text-blue-700' :
                          brochure.category === 'Internal Document' ? 'bg-yellow-100 text-yellow-700' :
                          brochure.category === 'Icons & Graphics' ? 'bg-green-100 text-green-700' :
                          brochure.category === 'Promotional Materials' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{brochure.category || 'Uncategorized'}</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{brochure.description}</p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        <button onClick={() => handleViewBrochure(brochure)} data-testid={`view-brochure-${brochure.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#1a4d2e] text-white rounded-lg text-sm hover:bg-[#2d5a3d]"><Eye className="w-4 h-4" /> View</button>
                        <button onClick={() => handleEditBrochure(brochure)} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Edit</button>
                        <button onClick={() => handleShareBrochure(brochure, 'native')} className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"><Share2 className="w-4 h-4" /> Share</button>
                        <button onClick={() => handleShareBrochure(brochure, 'email')} className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200" title="Email"><Mail className="w-4 h-4" /></button>
                        <button onClick={() => handleShareBrochure(brochure, 'sms')} className="flex items-center gap-1 px-2 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200" title="SMS"><MessageSquare className="w-4 h-4" /></button>
                        <button onClick={() => handleDeleteBrochure(brochure.id)} className="flex items-center gap-1 px-2 py-1.5 text-red-600 hover:bg-red-50 rounded-lg text-sm"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {selectedCategory !== 'All' && posBrochures.filter(b => b.category === selectedCategory).length === 0 && (
              <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
                <p className="text-gray-500">No documents in "{selectedCategory}" category.</p>
                <button onClick={() => setSelectedCategory('All')} className="text-[#1a4d2e] text-sm mt-2 hover:underline">Show all documents</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Ad Modal */}
      {showAddAdModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold text-[#1a4d2e] mb-4">Save New Ad</h3>
            <div className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Ad Name *</label><input type="text" value={newAd.name} onChange={(e) => setNewAd({...newAd, name: e.target.value})} placeholder="e.g., PriceHub Facebook Square" className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Platform</label><select value={newAd.platform} onChange={(e) => setNewAd({...newAd, platform: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg"><option value="">Select platform...</option><option value="Facebook">Facebook</option><option value="Instagram Feed">Instagram Feed</option><option value="Instagram Stories">Instagram Stories</option><option value="Facebook & Instagram">Facebook & Instagram</option><option value="Other">Other</option></select></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Image URL (optional)</label><input type="text" value={newAd.imageUrl} onChange={(e) => setNewAd({...newAd, imageUrl: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">HTML Content (optional)</label><textarea value={newAd.htmlContent} onChange={(e) => setNewAd({...newAd, htmlContent: e.target.value})} placeholder="<div>Paste HTML content here...</div>" rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Notes</label><textarea value={newAd.notes} onChange={(e) => setNewAd({...newAd, notes: e.target.value})} placeholder="Add any notes about this ad..." rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => { setShowAddAdModal(false); setNewAd({ name: '', platform: '', imageUrl: '', notes: '', htmlContent: '' }); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSaveAd} className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#1a4d2e]/90 transition-colors">Save Ad</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Brochure Modal */}
      {showEditBrochureModal && editingBrochure && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#1a4d2e]">Edit Brochure</h3>
              <button onClick={() => setShowEditBrochureModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Title</label><input type="text" value={brochureForm.title} onChange={(e) => setBrochureForm({...brochureForm, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><input type="text" value={brochureForm.description} onChange={(e) => setBrochureForm({...brochureForm, description: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label><select value={brochureForm.category} onChange={(e) => setBrochureForm({...brochureForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">{categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">HTML Content</label><textarea value={brochureForm.content} onChange={(e) => setBrochureForm({...brochureForm, content: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg h-64 font-mono text-sm" placeholder="Enter HTML content..." /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Live Preview</label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white min-h-[400px] overflow-auto">
                    {brochureForm.content ? <div dangerouslySetInnerHTML={{ __html: brochureForm.content }} /> : <p className="text-gray-400 text-center py-8">Enter HTML content to see preview</p>}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowEditBrochureModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleSaveBrochure} className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d5a3d]">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Brochure Modal */}
      {showCreateBrochureModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#1a4d2e]">Create New Brochure</h3>
              <button onClick={() => setShowCreateBrochureModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label><input type="text" value={brochureForm.title} onChange={(e) => setBrochureForm({...brochureForm, title: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g., Spring Sale Brochure" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label><input type="text" value={brochureForm.description} onChange={(e) => setBrochureForm({...brochureForm, description: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Brief description" /></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label><select value={brochureForm.category} onChange={(e) => setBrochureForm({...brochureForm, category: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg">{categoryOptions.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select></div>
                  <div><label className="block text-sm font-medium text-gray-700 mb-1">HTML Content</label><textarea value={brochureForm.content} onChange={(e) => setBrochureForm({...brochureForm, content: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg h-64 font-mono text-sm" placeholder="<div>Your brochure HTML content...</div>" /></div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Live Preview</label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white min-h-[400px] overflow-auto">
                    {brochureForm.content ? <div dangerouslySetInnerHTML={{ __html: brochureForm.content }} /> : <p className="text-gray-400 text-center py-8">Enter HTML content to see preview</p>}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowCreateBrochureModal(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleCreateBrochure} className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d5a3d]">Create Brochure</button>
            </div>
          </div>
        </div>
      )}

      {/* View Brochure Modal */}
      {showViewBrochureModal && viewingBrochure && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center gap-4 bg-[#1a4d2e]">
              <button onClick={() => { setShowViewBrochureModal(false); setViewingBrochure(null); }} data-testid="back-to-dashboard-btn" className="flex items-center gap-2 px-4 py-2 bg-white text-[#1a4d2e] rounded-lg hover:bg-gray-100 font-medium">Back to Dashboard</button>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">{viewingBrochure.title}</h3>
                {viewingBrochure.description && <p className="text-sm text-white/80">{viewingBrochure.description}</p>}
              </div>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleBrochureToImage(viewingBrochure, 'png', 'portrait')}
                  data-testid="download-view-png-btn"
                  className="flex items-center gap-1 px-3 py-2 bg-[#ff6b00] text-white rounded-lg text-sm hover:bg-[#ff6b00]/90 font-semibold"
                  title="Download portrait ad as PNG"
                >
                  <FileDown className="w-4 h-4" /> Portrait PNG
                </button>
                <button
                  onClick={() => handleBrochureToImage(viewingBrochure, 'png', 'landscape')}
                  data-testid="download-view-landscape-png-btn"
                  className="flex items-center gap-1 px-3 py-2 bg-[#ff6b00] text-white rounded-lg text-sm hover:bg-[#ff6b00]/90 font-semibold"
                  title="Download landscape ad as PNG"
                >
                  <FileDown className="w-4 h-4" /> Landscape PNG
                </button>
                <button
                  onClick={() => handleBrochureToImage(viewingBrochure, 'jpeg', 'portrait')}
                  data-testid="download-view-jpeg-btn"
                  className="flex items-center gap-1 px-3 py-2 bg-[#1a4d2e] text-white rounded-lg text-sm hover:bg-[#1a4d2e]/90 font-semibold"
                  title="Download portrait ad as JPEG"
                >
                  <FileDown className="w-4 h-4" /> JPEG
                </button>
                <button onClick={() => handleShareBrochure(viewingBrochure, 'native')} className="flex items-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700" title="Share"><Share2 className="w-4 h-4" /> Share</button>
                <button onClick={() => {
                  const isAd = savedAds.some(a => a.id === viewingBrochure.id);
                  setShowViewBrochureModal(false);
                  const item = viewingBrochure;
                  setViewingBrochure(null);
                  if (isAd) {
                    const originalAd = savedAds.find(a => a.id === item.id);
                    if (originalAd) handleEditAd(originalAd);
                  } else {
                    handleEditBrochure(item);
                  }
                }} className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Edit</button>
              </div>
            </div>
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              <div id="pdf-content" className="bg-white rounded-lg shadow-sm p-6 max-w-3xl mx-auto">
                <div dangerouslySetInnerHTML={{ __html: viewingBrochure.content }} />
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 bg-white flex justify-between items-center flex-wrap gap-2">
              <span className="text-sm text-gray-500">Category: {viewingBrochure.category || 'Uncategorized'}</span>
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => handleBrochureToImage(viewingBrochure, 'png', 'portrait')} data-testid="download-footer-png-btn" className="flex items-center gap-2 px-4 py-2 bg-[#ff6b00] text-white rounded-lg hover:bg-[#ff6b00]/90 font-medium">
                  <FileDown className="w-4 h-4" /> Portrait PNG
                </button>
                <button onClick={() => handleBrochureToImage(viewingBrochure, 'png', 'landscape')} data-testid="download-footer-landscape-png-btn" className="flex items-center gap-2 px-4 py-2 bg-[#ff6b00] text-white rounded-lg hover:bg-[#ff6b00]/90 font-medium">
                  <FileDown className="w-4 h-4" /> Landscape PNG
                </button>
                <button onClick={() => handleBrochureToImage(viewingBrochure, 'jpeg', 'portrait')} data-testid="download-footer-jpeg-btn" className="flex items-center gap-2 px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#1a4d2e]/90 font-medium">
                  <FileDown className="w-4 h-4" /> JPEG
                </button>
                <button onClick={() => { setShowViewBrochureModal(false); setViewingBrochure(null); }} className="px-6 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d5a3d] font-medium">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ad Modal */}
      {showEditAdModal && editingAd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-ad-modal">
          <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-xl font-bold text-[#1a4d2e]">Edit Ad</h3>
              <button onClick={() => { setShowEditAdModal(false); setEditingAd(null); }} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ad Name *</label>
                    <input
                      type="text"
                      value={editAdForm.name}
                      onChange={(e) => setEditAdForm({ ...editAdForm, name: e.target.value })}
                      data-testid="edit-ad-name-input"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                    <input
                      type="text"
                      value={editAdForm.platform}
                      onChange={(e) => setEditAdForm({ ...editAdForm, platform: e.target.value })}
                      data-testid="edit-ad-platform-input"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (optional)</label>
                    <input
                      type="text"
                      value={editAdForm.imageUrl}
                      onChange={(e) => setEditAdForm({ ...editAdForm, imageUrl: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={editAdForm.notes}
                      onChange={(e) => setEditAdForm({ ...editAdForm, notes: e.target.value })}
                      data-testid="edit-ad-notes-input"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">HTML Content</label>
                    <textarea
                      value={editAdForm.htmlContent}
                      onChange={(e) => setEditAdForm({ ...editAdForm, htmlContent: e.target.value })}
                      data-testid="edit-ad-html-input"
                      rows={16}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-xs"
                      placeholder="<div>Paste HTML content here...</div>"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Tip: use Ctrl/Cmd+F in the box to find the wording you want to change.
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Live Preview</label>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white min-h-[500px] max-h-[600px] overflow-auto">
                    {editAdForm.htmlContent ? (
                      <div dangerouslySetInnerHTML={{ __html: editAdForm.htmlContent }} />
                    ) : editAdForm.imageUrl ? (
                      <img src={editAdForm.imageUrl} alt={editAdForm.name} className="max-w-full rounded" />
                    ) : (
                      <p className="text-gray-400 text-center py-8">Enter HTML content or an Image URL to see preview</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => { setShowEditAdModal(false); setEditingAd(null); }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg"
              >Cancel</button>
              <button
                onClick={handleSaveEditedAd}
                data-testid="save-edited-ad-btn"
                className="px-4 py-2 bg-[#1a4d2e] text-white rounded-lg hover:bg-[#2d5a3d]"
              >Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isGeneratingPdf && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-xl p-8 text-center shadow-xl">
            <div className="animate-spin w-12 h-12 border-4 border-[#1a4d2e] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg font-medium text-[#1a4d2e]">Generating file…</p>
            <p className="text-sm text-gray-500 mt-2">Please wait</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingTab;
